import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import s from './Odometer.module.css';
import { cx } from './cx';

const DIGITS = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];

export interface OdometerProps {
  value: number;
  /** formats the number (locale grouping / decimals) — digits roll, other chars stay */
  format: (n: number) => string;
  /** --up flash when it grows, --down when it drops (header balance; colours follow the culture) */
  flash?: boolean;
  className?: string;
  style?: CSSProperties;
}

/** per-digit step of the roll */
const STEP_MS = 90;

/**
 * Rolling counter: digits roll like an odometer, right to left, 70ms stagger. Each column ticks through
 * whole digits (CSS steps()), so every frame reads as a real number — no half-scrolled glyphs
 * like «1 24¹,00» in the header balance. Reduced motion → no transition (value jumps).
 * Screen readers get the whole number.
 */
export function Odometer({ value, format, flash = true, className, style }: OdometerProps) {
  const text = format(value);
  const chars = Array.from(text);
  const prev = useRef(value);
  /** digit shown per column (keyed from the right) after the last commit — how many steps each column ticks */
  const shown = useRef(new Map<number, number>());
  // fixed per text: the flash re-render must not shorten a roll in progress
  const steps = useMemo(() => {
    const m = new Map<number, number>();
    Array.from(text).forEach((ch, i, all) => {
      if (!/\d/.test(ch)) return;
      const key = all.length - i;
      const d = Number(ch);
      m.set(key, Math.max(1, Math.abs(d - (shown.current.get(key) ?? d))));
    });
    return m;
  }, [text]);
  const [dir, setDir] = useState<'up' | 'down' | null>(null);

  useEffect(() => {
    if (value === prev.current) return;
    const d = value > prev.current ? 'up' : 'down';
    prev.current = value;
    if (!flash) return;
    setDir(d);
    const t = setTimeout(() => setDir(null), 60);
    return () => clearTimeout(t);
  }, [value, flash]);

  useEffect(() => {
    const m = new Map<number, number>();
    chars.forEach((ch, i) => {
      if (/\d/.test(ch)) m.set(chars.length - i, Number(ch));
    });
    shown.current = m;
  });

  return (
    <span
      className={cx(s.odo, dir === 'up' && s.up, dir === 'down' && s.down, className)}
      style={style}
      role="text"
      aria-label={text}
      data-value={value}
    >
      {chars.map((ch, i) => {
        // key from the right so columns keep identity when the number gets longer
        const key = chars.length - i;
        if (!/\d/.test(ch)) {
          return (
            <span key={`s${key}`} className={s.sep} aria-hidden="true">
              {ch}
            </span>
          );
        }
        const d = Number(ch);
        const n = steps.get(key) ?? 1;
        return (
          <span
            key={`d${key}`}
            className={s.col}
            aria-hidden="true"
            data-digit={d}
            style={{
              transform: `translateY(${-1.2 * d}em)`,
              transitionDelay: `${(chars.length - i) * 70}ms`,
              transitionDuration: `${n * STEP_MS}ms`,
              transitionTimingFunction: `steps(${n}, jump-start)`,
            }}
          >
            {DIGITS.map((n) => (
              <span key={n}>{n}</span>
            ))}
          </span>
        );
      })}
    </span>
  );
}
